import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';

import { CircularProgress, Grid } from '@material-ui/core';

import { fecthActualRevision, fecthAllRevisions, fetchDeadLine } from 'app/store/slices/revisions';
import { fetchServiceDocuments } from 'app/store/slices/services';
import { fetchLicenseVehicleDocuments } from 'app/store/slices/vehicle';
import { showLoading, hideLoading } from 'app/store/slices/settings';
import { updateRequestDetails } from 'app/store/slices/requests';
import { errorHandler } from 'app/utils/errorHandler';

import { SERVICES_CODES, SERVICES_GROUPS } from 'app/config/services';
import { STATUS_CODES } from 'app/config/status';
import { INTERNAL_CONTEXT } from 'app/config/urls';

import revisionsEndpoints from 'app/api/services/revisionsEndpoints';
import requestEndpoints from 'app/api/services/requestEndpoints';
import notification from 'app/config/notification';

import DateInformation from 'app/internalPages/RequestDetails/components/Documentation/components/DateInformation';
import { prepareData, validation } from 'app/internalPages/RequestDetails/components/Documentation/functions/revisions';

const withDocumentRevision = (BaseComponent) => (props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { intl, filesStore } = props;
    const [loading, setLoading] = useState(true);
    const [loadingDocumentation, setLoadingDocumentation] = useState(true);
    const [generalObsertations, setGeneralObsertations] = useState('');
    const [finalDate, setFinalDate] = useState(new Date());
    const [tempJsonSolicitacao, setTempJsonSolicitacao] = useState({});

    const actualRevision = useSelector((state) => state.revisions.details);

    const request = useSelector((state) => state.requests.details);

    useEffect(() => {
        if (actualRevision.deRevisao) setGeneralObsertations(actualRevision.deRevisao);
    }, [actualRevision]);

    useEffect(() => {
        const init = () => {
            setTempJsonSolicitacao(JSON.parse(request.jsonSolicitacao));
        };
        init();
    }, [request]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const initialValues = JSON.parse(request.jsonSolicitacao);
            const promisses = [];

            if (request.cdSituacao !== STATUS_CODES.EM_CONSTRUCAO && request.cdSolicitacao) {
                promisses.push(dispatch(fecthActualRevision(request.cdSolicitacao, '')));
                promisses.push(dispatch(fecthAllRevisions(request.cdSolicitacao, '')));
                promisses.push(dispatch(fetchDeadLine()));
            }

            let cdServico = request.cdServico || request.servico?.cdServico;
            if (request.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA && initialValues.cdServicoOrigem) {
                cdServico = initialValues.cdServicoOrigem;
            }
            const objPromisseDocuments = {
                cdServico,
            };
            if (initialValues.cdTipoPessoaFisica || initialValues.empresa?.cdTipoPessoaFisica) {
                objPromisseDocuments.cdTipoPessoaFisica = parseInt(
                    initialValues.cdTipoPessoaFisica || initialValues.empresa.cdTipoPessoaFisica,
                );
            }
            if (initialValues.cdTipoEmpresa || initialValues.empresa?.cdTipoEmpresa) {
                objPromisseDocuments.cdTipoEmpresa = parseInt(
                    initialValues.cdTipoEmpresa || initialValues.empresa?.cdTipoEmpresa,
                );
            }
            promisses.push(dispatch(fetchServiceDocuments(objPromisseDocuments)));
            if (SERVICES_GROUPS.LICENSE.includes(request.cdServico)) {
                promisses.push(dispatch(fetchLicenseVehicleDocuments(cdServico)));
            }

            await Promise.all(promisses);
            setLoadingDocumentation(false);
            setLoading(false);
        };
        init();
    }, [dispatch, request]);

    const onCancel = () => {
        history.push(INTERNAL_CONTEXT);
    };

    const onSave = async ({ fromRequestRevision, documentsToRevision }) => {
        let arrayDocuments = [];
        documentsToRevision.forEach((docRev) => {
            const docs = docRev.documents || [];
            arrayDocuments = [...arrayDocuments, ...docs];
        });

        if (!validation(arrayDocuments)) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.ERROS_SAVING_REVISION' }), {
                toastId: 'ERROS_SAVING_REVISION',
                type: 'error',
            });
            return;
        }

        dispatch(showLoading());
        try {
            const sendingData = prepareData({
                request,
                documentsToRevision: arrayDocuments,
                actualRevision,
                generalObsertations,
                finalDate,
            });

            const response = await revisionsEndpoints.saveDocumentRevision(sendingData);
            if (response.status === 200) {
                if (!fromRequestRevision) {
                    notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_SAVE_REVISION' }), {
                        toastId: 'SUCCESSFUL_SAVE_REVISION',
                        type: 'success',
                    });
                    dispatch(showLoading());
                    let requestToUpdate = { ...request };
                    if (SERVICES_GROUPS.LICENSE.includes(request.cdServico)) {
                        if (Object.keys(tempJsonSolicitacao).length > 0) {
                            requestToUpdate.jsonSolicitacao = JSON.stringify(tempJsonSolicitacao);
                            const responseRequest = await requestEndpoints.saveRequest(requestToUpdate, '');
                            if (responseRequest.status === 200 && responseRequest.data) {
                                requestToUpdate = responseRequest.data;
                            }
                        }
                    } else {
                        const responseRequest = await requestEndpoints.findRequest(
                            { cdSolicitacao: request.cdSolicitacao },
                            '',
                        );
                        if (responseRequest.status === 200 && responseRequest.data) {
                            requestToUpdate = responseRequest.data;
                        }
                    }
                    await dispatch(updateRequestDetails(requestToUpdate));
                    setLoadingDocumentation(false);
                    dispatch(hideLoading());
                }
                return response.data.cdRevisaoSolicitacao;
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
            dispatch(hideLoading());
        }
    };

    const handleRequestReview = async (documentsToRevision) => {
        const cdRevisaoSolicitacao = await onSave({
            fromRequestRevision: true,
            documentsToRevision: documentsToRevision,
        });
        try {
            if (!cdRevisaoSolicitacao) {
                // eslint-disable-next-line no-throw-literal
                throw String('Error');
            }
            const response = await revisionsEndpoints.requestRevision(cdRevisaoSolicitacao);
            if (response.status === 200) {
                notification(intl.formatMessage({ id: 'MESSAGES.REQUEST.REQUEST_REVISION_SENT' }), {
                    toastId: 'REQUEST_REVISION_SENT',
                    type: 'success',
                });
            }
            history.push(INTERNAL_CONTEXT);
        } catch (error) {
            notification(intl.formatMessage({ id: 'MESSAGES.REQUEST.PROBLEM_REVISION_SEND' }), {
                toastId: 'PROBLEM_REVISION_SEND',
                type: 'error',
            });
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        dispatch(hideLoading());
    };

    if (loading || loadingDocumentation) {
        return (
            <Grid item xs={12} style={{ textAlign: 'center' }}>
                <CircularProgress size={18} />
            </Grid>
        );
    }

    return (
        <>
            <Formik initialValues={{}} onSubmit={() => {}}>
                {(props) => {
                    return (
                        <Form style={{ position: 'relative', paddingBottom: '60px' }}>
                            <DateInformation {...props} finalDate={finalDate} setFinalDate={setFinalDate} />

                            <Grid container spacing={3}>
                                <BaseComponent
                                    {...props}
                                    loading={loading}
                                    filesStore={filesStore}
                                    generalObsertations={generalObsertations}
                                    setGeneralObsertations={setGeneralObsertations}
                                    onSave={onSave}
                                    onCancel={onCancel}
                                    handleRequestReview={handleRequestReview}
                                    tempJsonSolicitacao={tempJsonSolicitacao}
                                    setTempJsonSolicitacao={setTempJsonSolicitacao}
                                />
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default withDocumentRevision;
